import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SignDetail } from 'src/app/model/sign/sign-detail';
import { SignStickerControllerRequestService } from '../../../../../services/api/sign-associations/implementations/sign-sticker-controller-request.service';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { SignStickerOverview } from 'src/app/model/sign/sticker/sign-sticker-overview';
import { ProperPageResult } from 'src/app/model/proper-page-result';
import {
  ModalService,
  ObservableHandlerPair
} from 'src/app/services/modal.service';
import { SignStickerEditComponent } from '../../edit-association/sign-sticker/sign-sticker-edit.component';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SignStickerDetail } from 'src/app/model/sign/sticker/sign-sticker-detail';
import { INT_MAX_SIGNED_32_BIT_VALUE } from 'src/globals/globals';
import { SignStickerAsset } from 'src/app/model/sign/sticker/sign-sticker-asset';
import { Lightbox } from 'ngx-lightbox';

@Component({
  selector: 'sign-sticker-overview',
  templateUrl: './sign-sticker-overview.component.html',
  styleUrls: [
    './sign-sticker-overview.component.scss',
    '../sign-associations-overview-shared-style.scss'
  ]
})
export class SignStickerOverviewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy {
  @Input()
  sign: SignDetail;

  loading: boolean = true;
  signStickers: SignStickerOverview[];

  private getSignStickerSubject: Subject<any>;

  constructor(
    private signStickerControllerRequestService: SignStickerControllerRequestService,
    private modalService: ModalService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.getSignStickerSubject = new Subject<any>();

    this.addSubscription(
      this.getSignStickerSubject.pipe(
        startWith({}),
        switchMap(async () => {
          this.loading = true;
          return await this.signStickerControllerRequestService.getPaged(
            this.sign.id,
            null,
            'id',
            true,
            INT_MAX_SIGNED_32_BIT_VALUE,
            0,
            false
          );
        }),
        catchError(() => {
          return of({});
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ProperPageResult<SignStickerOverview>) => {
        this.signStickers = res == null ? [] : res.items;
        this.loading = false;
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getSignStickerSubject != null) {
      this.getSignStickerSubject.complete();
    }
  }

  addItem(): void {
    this.openEditModal();
  }

  editItem(signSticker: SignStickerOverview): void {
    this.openEditModal(signSticker.id);
  }

  private openEditModal(stickerId?: number): void {
    const assocObject = stickerId == null ? null : new SignStickerDetail();
    if (assocObject != null) {
      assocObject.id = stickerId;
    }
    this.modalService.openComponentInModal(
      SignStickerEditComponent,
      [
        { key: 'signId', value: this.sign.id },
        { key: 'assocObject', value: assocObject }
      ],
      new ObservableHandlerPair(
        modalRef => modalRef.componentInstance.saveSuccessEvent,
        (res: SignStickerDetail) => {
          if (res != null) {
            this.getSignStickerSubject.next();
          }
        }
      ),
      { width: '750px', maxHeight: '90vh' }
    );
  }

  deleteItem(signSticker: SignStickerDetail): void {
    this.modalService.confirmDialog(
      'Sticker verwijderen',
      'Bent u zeker dat u deze sticker wilt verwijderen?',
      (b: boolean) => {
        if (b) {
          this.performDelete(signSticker);
        }
      }
    );
  }

  private async performDelete(signSticker: SignStickerDetail): Promise<void> {
    this.loading = true;
    try {
      await this.signStickerControllerRequestService.archive(signSticker.id);
      this.getSignStickerSubject.next();
    } catch (err) {
      this.loading = false;
      throw err;
    }
  }

  openLightbox(signStickerAssets: SignStickerAsset[]): void {
    let albums = [];
    for (let asset of signStickerAssets) {
      if (asset.archived) {
        continue;
      }
      const src = '/api/assets/' + asset.asset.uuid;
      const caption = 'Ontwerp';
      const thumb = '/api/assets/' + asset.asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }
}
